/*
 * @file: index.js
 * @description: It Contain rest functions for api call .
 * @author: Poonam
 */

import api from "../../api/apiClient";
import { setAuthorizationToken } from "../auth";
import { toast } from "react-toastify";
import loader from "../loader";
import environment from "../../environment";
import methodModel from "../methods";
import Swal from "sweetalert2";

var config = {
  headers: { "Content-Type": "application/json" },
};

var baseUrl = environment?.api || 'https://kindaapi.jcsoftwaresolution.in/';

const getFullUrl = (url1, base) => {
  if (url1 && (url1.startsWith('http://') || url1.startsWith('https://'))) {
    return url1;
  }
  const b = base || baseUrl || '';
  if (!b) return url1;
  const cleanBase = b.endsWith('/') ? b : `${b}/`;
  const cleanUrl = url1.startsWith('/') ? url1.slice(1) : url1;
  return cleanBase + cleanUrl;
};


const extractErrorMessage = (err) => {
  if (!err) return "";
  if (typeof err === "string") {
    const trimmed = err.trim();
    if (trimmed.startsWith("<")) return ""; // ignore HTML error responses (e.g. 502/504)
    return trimmed;
  }
  if (typeof err.error === "string") return err.error.trim();
  if (err.error && typeof err.error.message === "string") return err.error.message.trim();
  if (err.error && typeof err.error.error === "string") return err.error.error.trim();
  if (
    typeof err.message === "string" &&
    err.message.trim().toLowerCase() !== "server error" &&
    err.message.trim().toLowerCase() !== "network error"
  ) {
    return err.message.trim();
  }
  if (typeof err.msg === "string") return err.msg.trim();
  if (Array.isArray(err.errors) && err.errors.length > 0) {
    const firstErr = err.errors[0];
    if (typeof firstErr === "string") return firstErr.trim();
    if (firstErr && typeof firstErr.msg === "string") return firstErr.msg.trim();
    if (firstErr && typeof firstErr.message === "string") return firstErr.message.trim();
  }
  if (err.data) {
    return extractErrorMessage(err.data);
  }
  return "";
};

const isApiFailed = (data) => {
  if (data === null || data === undefined) return true;
  if (data?.success === false) return true;
  if (data?.status === false || data?.status === "error" || data?.status === "failed") return true;
  return false;
};

const handleError = (err, hideError = false) => {
  if (err) {
    if (
      (err && err.error && err.error.code == 401) ||
      err?.code == 401 ||
      err?.status == 401
    ) {
      // dispatch(logout());
      localStorage.removeItem("persist:admin-app");
      localStorage.removeItem("token");
      document.getElementById("handleLogout")?.click();
      hideError = true;
      methodModel.route("/");
    }
  }

  let message = extractErrorMessage(err);
  if (
    !message ||
    message.toLowerCase() === "server error" ||
    message.toLowerCase() === "network error"
  ) {
    message = "Something went wrong";
  }

  const lowerMsg = (message || "").toLowerCase();
  const isLimitError =
    lowerMsg.includes("reached your limit") ||
    lowerMsg.includes("reached limit") ||
    lowerMsg.includes("limit reached") ||
    lowerMsg.includes("exceeded your limit") ||
    lowerMsg.includes("limit exceeded") ||
    lowerMsg.includes("upgrade your plan") ||
    lowerMsg.includes("upgrade the plan") ||
    lowerMsg.includes("don't have limits") ||
    lowerMsg.includes("no active plan");

  if (isLimitError) {
    hideError = true;
    Swal.fire({
      title: "Plan Limit Reached",
      text: message || "You have reached your limit. If you want to add more, you need to upgrade your plan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#494f9f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Upgrade Plan",
      cancelButtonText: "Close",
    }).then((result) => {
      if (result.isConfirmed) {
        methodModel.route("/plan");
      }
    });
  }

  if (!Boolean(hideError)) toast.error(message || "Something went wrong");
};


class ApiClient {
  static post(url1, payload, params = {}, base = "", hideError = false) {
    let url = getFullUrl(url1, base);

    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      api
        .post(url, payload, { ...config, params: params })
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static put(url1, payload, base = "", hideError = false) {
    let url = getFullUrl(url1, base);
    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      api
        .put(url, payload, config)
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static putWithoutError(url1, payload, base = "") {
    let url = getFullUrl(url1, base);
    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      api
        .put(url, payload, config)
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            // handleError(eres.data);
            fulfill(eres.data);
          } else {
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static get(url1, params = {}, base = "", hideError = false) {
    let url = getFullUrl(url1, base);
    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      api
        .get(url, { ...config, params: params })
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static delete(url1, params = {}, base = "", hideError = false) {
    let url = getFullUrl(url1, base);

    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      api
        .delete(url, { ...config, params: params })
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static allApi(url, params, method = "get", hideError = false) {
    if (method == "get") {
      return this.get(url, params, "", hideError);
    } else if (method == "put") {
      return this.put(url, params, "", hideError);
    }
    if (method == "post") {
      return this.post(url, params, {}, "", hideError);
    }
  }

  /*************** Form-Data Method ***********/
  static postFormData(url1, params, hideError = false) {
    let configupdate = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    let url = getFullUrl(url1);
    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      var body = new FormData();
      let oArr = Object.keys(params || {});
      oArr.map((itm) => {
        body.append(itm, params[itm]);
      });

      api
        .post(url, body, configupdate)
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static postFormFileData(url1, params, base = "", hideError = false) {
    let configupdate = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    let url = getFullUrl(url1, base);
    setAuthorizationToken(api);
    return new Promise(function (fulfill, reject) {
      var body = new FormData();
      let oArr = Object.keys(params || {});
      oArr.map((itm) => {
        body.append(itm, params[itm]);
      });

      api
        .post(url, body, configupdate)
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }

  static multiImageUpload(url1, files, params = {}, key = "file", hideError = false) {
    let configupdate = {
      headers: { "Content-Type": "multipart/form-data" },
      params: params,
    };
    let url = getFullUrl(url1);
    setAuthorizationToken(api);
    let body = new FormData();

    let i = 0;
    for (let item of files) {
      let file = files.item(i);
      body.append(key, file);
      i++;
    }
    return new Promise(function (fulfill, reject) {
      api
        .post(url, body, configupdate)
        .then(function (response) {
          if (isApiFailed(response?.data)) {
            handleError(response?.data, hideError);
          }
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres?.data || error, hideError);
            fulfill({ ...(eres?.data || {}), success: false });
          } else {
            handleError(error, hideError);
            fulfill({ success: false, message: "Something went wrong" });
          }
        });
    });
  }
}

export { api };
export default ApiClient;

